import {useState} from 'react';
import classNames from 'classnames';
import {
  CartCheckoutButton,
  CartLineImage,
  CartLineProductTitle,
  CartLineQuantityAdjustButton,
  CartLinePrice,
  CartLineQuantity,
  ProductOptionsProvider,
  CartCost,
  useCart,
  CartLines,
  useCartLine,
} from '@shopify/hydrogen';
import {Dialog} from '@headlessui/react';

import {useCartUI} from './CartUIProvider.client';
import {BUTTON_PRIMARY_CLASSES} from './Button.client';

import XIcon from './Icons/XIcon';
import TrashIcon from './Icons/TrashIcon';
import CartIcon from './Icons/CartIcon';
import {usePageContext} from '../contexts/PageContext.client';
import {ProductDefaults, getProductFromVariantId} from '../utils/product';
import {RecordClickEvent} from './Analytics/RecordClickEvent.client';
import {MonorailEvent} from '../utils/monorail.client';
import {ShoppingBagEvent} from '../utils/shoppingBagAnalytics';

import ProductModal from './ProductModal/ProductModal.client';
import {base64Decode} from 'shared/utils/base64';

/**
 * A client component that contains the merchandise that a customer intends to purchase, and the estimated cost associated with the cart
 */
export default function Cart() {
  const {isCartOpen, closeCart} = useCartUI();
  const {totalQuantity} = useCart();

  return (
    <Dialog open={isCartOpen} onClose={closeCart}>
      <Dialog.Overlay
        className={classNames(
          'fixed',
          'z-20',
          'inset-0',
          'bg-gray-50',
          'opacity-75',
        )}
      />
      <div
        className={classNames(
          'fixed',
          'top-0',
          'left-0',
          'right-0',
          'bottom-0',
          'z-50',
          'flex',
          'h-full',
          'w-full',
          'flex-col',
          'rounded-b-lg',
          'bg-gray-50',
          'shadow-2xl',
          'md:left-auto',
          'md:h-auto',
          'md:w-[470px]',
          totalQuantity === 0 ? 'overflow-hidden' : 'overflow-y-scroll',
        )}
      >
        <CartHeader />
        {totalQuantity === 0 ? (
          <CartEmpty />
        ) : (
          <>
            <CartItems />
            <CartFooter />
          </>
        )}
      </div>
    </Dialog>
  );
}

function CartHeader() {
  const {closeCart} = useCartUI();
  return (
    <header
      className={classNames(
        'sticky',
        'top-0',
        'flex',
        'items-center',
        'justify-between',
        'border-b',
        'border-gray-300',
        'bg-white',
        'py-3',
        'px-6',
      )}
    >
      <span className={classNames('text-md', 'py-4')}>Your shopping bag</span>
      <button type="button" onClick={closeCart}>
        <XIcon />
        <span className="sr-only">Close cart</span>
      </button>
    </header>
  );
}

function CartItems() {
  return (
    <div
      className={classNames('px-7', 'flex-grow')}
      role="table"
      aria-label="Shopping cart"
    >
      <div role="row" className="sr-only">
        <div role="columnheader">Product image</div>
        <div role="columnheader">Product details</div>
        <div role="columnheader">Price</div>
      </div>
      <CartLines>
        <LineInCart />
      </CartLines>
    </div>
  );
}

function LineInCart() {
  const {merchandise, quantity} = useCartLine();
  merchandise.id = base64Decode(merchandise.id);
  const {productLinks, id: pageId} = usePageContext();
  const {id: cartId} = useCart();

  const [isOpen, setIsOpen] = useState(false);
  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const parentProduct = getProductFromVariantId(productLinks, merchandise.id);
  const hasParentProduct =
    Boolean(parentProduct) && Object.keys(parentProduct).length !== 0;

  return (
    <>
      <div
        role="row"
        data-testid="cart-line-item"
        className={classNames(
          'flex',
          'border-b',
          'border-gray-300',
          'py-7',
          'text-gray-900',
          'last:border-b-0',
        )}
      >
        <div role="cell" className={classNames('flex-shrink-0', 'mr-7')}>
          <CartLineImage
            data-testid="cart-line-item-image"
            onClick={openModal}
            className={classNames(
              {
                'cursor-pointer': hasParentProduct,
                'pointer-events-none': !hasParentProduct,
              },
              'rounded-xl',
              'border',
              'border-black',
              'border-opacity-5',
              'bg-white',
            )}
            alt={parentProduct?.title}
            width={98}
            height={98}
          />
        </div>
        <div
          role="cell"
          className={classNames(
            'flex-grow-1',
            'mr-4',
            'flex',
            'w-full',
            'flex-col',
            'items-start',
            'justify-between',
          )}
        >
          <CartLineProductTitle
            onClick={openModal}
            className={classNames(
              {
                'cursor-pointer': hasParentProduct,
                'pointer-events-none': !hasParentProduct,
              },
              'text-md',
              'font-medium',
            )}
          />
          <div className="text-sm">
            {merchandise.selectedOptions.map(({value}) => {
              if (value === ProductDefaults.VALUE) {
                return <></>;
              }
              return (
                <div
                  key={value}
                  className={classNames(
                    'inline-block',
                    'text-gray-500',
                    "after:content-['/']",
                    "last:after:content-['']",
                  )}
                >
                  <span className="px-1">{value}</span>
                </div>
              );
            })}
          </div>
          <CartItemQuantity />
        </div>
        <div
          role="cell"
          className={classNames(
            'flex',
            'flex-col',
            'items-end',
            'justify-between',
          )}
        >
          <CartLinePrice className="text-md" />
          <RecordClickEvent
            event={MonorailEvent.TrackShoppingBag}
            eventType={ShoppingBagEvent.ClickRemoveFromBag}
            data={{
              pageId,
              cartId,
              currency: merchandise.priceV2.currencyCode,
              products: [
                {
                  productVariantId: merchandise.id,
                  productId: parentProduct?.id,
                  productName: merchandise.title,
                  productPrice: merchandise.priceV2.amount,
                  productQuantity: quantity,
                },
              ],
            }}
          >
            <CartLineQuantityAdjustButton
              adjust="remove"
              data-testid="cart-line-item-remove"
              aria-label="Remove from cart"
              className={classNames(
                'disabled:pointer-events-all',
                'rounded-md',
                'border',
                'border-red-500',
                'p-2',
                'hover:bg-red-100',
                'disabled:cursor-wait',
              )}
            >
              <TrashIcon />
            </CartLineQuantityAdjustButton>
          </RecordClickEvent>
        </div>
      </div>
      {hasParentProduct && (
        <ProductOptionsProvider
          data={parentProduct}
          initialVariantId={merchandise.id}
        >
          <ProductModal
            show={isOpen}
            closeModal={closeModal}
            product={parentProduct}
            title={parentProduct.title}
          />
        </ProductOptionsProvider>
      )}
    </>
  );
}

function CartItemQuantity() {
  return (
    <div
      className={classNames(
        'mt-2',
        'flex',
        'items-center',
        'overflow-auto',
        'rounded',
        'border',
        'border-gray-300',
      )}
    >
      <CartLineQuantityAdjustButton
        adjust="decrease"
        aria-label="Decrease quantity"
        data-testid="cart-line-item-decrease"
        className={classNames(
          'disabled:pointer-events-all',
          'p-2',
          'disabled:cursor-wait',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={classNames('h-5', 'w-5', 'text-gray-400')}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </CartLineQuantityAdjustButton>
      <CartLineQuantity
        as="div"
        data-testid="cartline-quantity"
        className={classNames('p-2', 'text-gray-900', 'text-xs', 'text-center')}
      />
      <CartLineQuantityAdjustButton
        adjust="increase"
        data-testid="cart-line-item-increase"
        aria-label="Increase quantity"
        className={classNames(
          'p-2',
          'text-gray-400',
          'disabled:pointer-events-all',
          'disabled:cursor-wait',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={classNames('h-5', 'w-5')}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            clipRule="evenodd"
          />
        </svg>
      </CartLineQuantityAdjustButton>
    </div>
  );
}

function CartFooter() {
  const pageCtx = usePageContext();
  const {
    id: cartId,
    cost: {subtotalAmount: {amount, currencyCode}} = {
      subtotalAmount: {},
    },
    lines,
  } = useCart();

  return (
    <footer
      className={classNames(
        'sticky',
        'bottom-0',
        'border-t',
        'border-black',
        'border-opacity-5',
      )}
    >
      <div
        className={classNames(
          'h-50',
          'relative',
          'bg-white',
          'py-4',
          'px-7',
          'text-gray-900',
        )}
      >
        <div role="table" aria-label="Cost summary">
          <div role="row" className={classNames('flex', 'justify-between')}>
            <span className="font-semibold" role="rowheader">
              Subtotal
            </span>
            <CartCost
              amountType="subtotal"
              role="cell"
              className="text-right"
            />
          </div>
          <div role="row" className={classNames('flex', 'justify-between')}>
            <span
              className={classNames('py-2', 'text-sm', 'text-gray-500')}
              role="rowheader"
            >
              Shipping, taxes, and discount codes calculated at checkout.
            </span>
          </div>
        </div>
        <RecordClickEvent
          event={MonorailEvent.TrackShoppingBag}
          eventType={ShoppingBagEvent.ClickProceedToCheckout}
          data={{
            pageId: pageCtx.id,
            cartId,
            currency: currencyCode,
            products: lines.map((line) => {
              return {
                productVariantId: line.merchandise.id,
                productId: getProductFromVariantId(
                  pageCtx.productLinks,
                  line.merchandise.id,
                )?.id,
                productName: line.merchandise.product.title,
                productPrice: line.merchandise.priceV2.amount,
                productQuantity: line.quantity,
              };
            }),
            totalValue: amount ? parseFloat(amount) : undefined,
          }}
        >
          <CartCheckoutButton
            data-testid="cart-checkout"
            className={classNames(BUTTON_PRIMARY_CLASSES, 'mt-2')}
          >
            Continue to checkout
          </CartCheckoutButton>
        </RecordClickEvent>
      </div>
    </footer>
  );
}

function CartEmpty() {
  const {closeCart} = useCartUI();
  return (
    <div
      className={classNames(
        'mt-[-80px]',
        'flex',
        'h-full',
        'flex-col',
        'items-center',
        'justify-center',
        'p-7',
      )}
    >
      <CartIcon width={120} height={120} stroke={'#969C9C'} />
      <p
        className={classNames(
          'mb-4',
          'text-center',
          'text-lg',
          'text-gray-500',
        )}
      >
        Your bag is empty
      </p>
      <div className={classNames('flex', 'w-36')}>
        <button
          type="button"
          onClick={closeCart}
          className={classNames(BUTTON_PRIMARY_CLASSES, 'px-2', 'py-2')}
        >
          Back to profile
        </button>
      </div>
    </div>
  );
}
